import React, {useState} from 'react';
import {Box, Typography, Accordion, AccordionSummary, Button} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SamplesSummaryTable, {SamplesSummaryTableRow} from '../SamplesSummaryTable';

const DBReview: React.FC<any> = ({formData, handleNext, samples}) => {
    const userSetDBAnalysisConfigurations = [
        {
            category: 'Samples',
            parameters: samples
                .filter((s: any) => formData.sample_ids.includes(s.id))
                .map((s: any) => {
                    return {
                        sample_name: s.name,
                        factor: s.other_categories.Factor ?? '',
                        tissue: s.other_categories.Tissue ?? '',
                        condition: s.other_categories.Condition ?? '',
                        replicate: s.other_categories.Replicate ?? '',
                        treatment: s.other_categories.Treatment ?? '',
                        sample_type: s.other_categories.SampleType ?? '',
                        control_name: s.other_categories.ControlName ?? '',
                        peaks: s.peaks
                    };
                })
        },
        {
            category: 'Variables',
            parameters: Object.entries(formData.selected_group_options).map(([group, levels]) => ({
                name: group,
                parameters: {
                    values: levels
                }
            }))
        },
        {
            category: 'Settings',
            parameters: [
                {
                    values: [
                        {
                            name: 'Significance Threshold',
                            value: formData.differential_binding_analysis.significance_threshold
                        },
                        {
                            name: 'Summits',
                            value: formData.differential_binding_analysis.summits
                        },
                        {
                            name: 'TSS Region',
                            value: formData.differential_binding_analysis.tss_region
                        },
                        {
                            name: 'Level',
                            value: formData.differential_binding_analysis.level
                        },
                        {
                            name: 'Overlap',
                            value: formData.differential_binding_analysis.overlap
                        },
                        {
                            name: 'Genomic Annotation Priority',
                            value: formData.differential_binding_analysis.genomic_annotation_priority
                        }
                    ]
                }
            ]
        }
    ];

    const [listStates, setListStates] = useState(new Array(userSetDBAnalysisConfigurations.length).fill({open: true, editMode: false}));

    const sampleHeaders = ['Sample Name', 'Peaks', 'Factor', 'Condition', 'Replicate', 'Treatment', 'Sample Type', 'Control Name'];
    const sampleRows: SamplesSummaryTableRow[] = userSetDBAnalysisConfigurations[0].parameters.map((param: any) => ({
        key: param.sample_name,
        cells: [
            param.sample_name,
            param.peaks,
            param.factor,
            param.condition,
            param.replicate,
            param.treatment,
            param.sample_type,
            param.control_name
        ]
    }));

    // TODO: refactor this humongous return into one with subcomponents for readability
    return (
        <Box
            display="flex"
            flexDirection="column"
            gap="36px"
            marginBottom="24px"
            bgcolor={'surface-container-lowest'}
            p={3}
            borderRadius={'12px'}>
            <Box borderRadius="20px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                <Accordion
                    expanded={listStates[0].open}
                    sx={{
                        backgroundColor: 'unset',
                        boxShadow: 'unset',
                        '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                            minHeight: '0'
                        },
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                            borderBottom: '1px solid',
                            borderColor: 'outline-variant'
                        }
                    }}>
                    <AccordionSummary
                        aria-controls="panel1d-content"
                        expandIcon={<ArrowDropUpIcon />}
                        id="panel1d-header"
                        onClick={() =>
                            setListStates(state =>
                                state.map((item, index) => {
                                    if (index === 0) {
                                        return {...item, open: !item.open};
                                    }
                                    return item;
                                })
                            )
                        }
                        sx={{
                            backgroundColor: 'surface-container-high',
                            padding: '4px 4px 4px 16px',
                            height: '0',
                            minHeight: '0',
                            gap: '12px',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                alignItems: 'center',
                                margin: 0
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {}
                        }}>
                        <Typography variant="title" size="medium" width="100%">
                            {userSetDBAnalysisConfigurations[0].category}
                        </Typography>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                e.stopPropagation();
                                handleNext(1);
                            }}>
                            Edit
                        </Button>
                    </AccordionSummary>
                    <SamplesSummaryTable headers={sampleHeaders} rows={sampleRows} />
                </Accordion>
            </Box>

            <Box borderRadius="20px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                <Accordion
                    expanded={listStates[1].open}
                    sx={{
                        backgroundColor: 'unset',
                        boxShadow: 'unset',
                        '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                            minHeight: '0'
                        },
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                            borderBottom: '1px solid',
                            borderColor: 'outline-variant'
                        }
                    }}>
                    <AccordionSummary
                        aria-controls="panel1d-content"
                        expandIcon={<ArrowDropUpIcon />}
                        id="panel1d-header"
                        onClick={() =>
                            setListStates(state =>
                                state.map((item, index) => {
                                    if (index === 1) {
                                        return {...item, open: !item.open};
                                    }
                                    return item;
                                })
                            )
                        }
                        sx={{
                            backgroundColor: 'surface-container-high',
                            padding: '4px 4px 4px 16px',
                            height: '0',
                            minHeight: '0',
                            gap: '12px',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                alignItems: 'center',
                                margin: 0
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {}
                        }}>
                        <Typography variant="title" size="medium" width="100%">
                            {userSetDBAnalysisConfigurations[1].category}
                        </Typography>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                e.stopPropagation();
                                handleNext(2);
                            }}>
                            Edit
                        </Button>
                    </AccordionSummary>
                    <Box padding="24px">
                        {userSetDBAnalysisConfigurations[1].parameters.map((param: any) => {
                            if ('parameters' in param) {
                                return (
                                    <Box display="flex" gap={3}>
                                        <Typography variant="title" size="medium" fontWeight={600} width={380} mt={1}>
                                            {param.name}
                                        </Typography>
                                        <Typography variant="body" size="large" width={400} mt={1}>
                                            {param.parameters.values
                                                .sort((a: any, b: any) => a.length - b.length)
                                                .map((v: any) => v.split(',').join(', '))
                                                .join(', ')}
                                        </Typography>
                                    </Box>
                                );
                            }
                            return null;
                        })}
                    </Box>
                </Accordion>
            </Box>

            <Box borderRadius="20px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                <Accordion
                    expanded={listStates[2].open}
                    sx={{
                        backgroundColor: 'unset',
                        boxShadow: 'unset',
                        '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                            minHeight: '0'
                        },
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                            borderBottom: '1px solid',
                            borderColor: 'outline-variant'
                        }
                    }}>
                    <AccordionSummary
                        aria-controls="panel1d-content"
                        expandIcon={<ArrowDropUpIcon />}
                        id="panel1d-header"
                        onClick={() =>
                            setListStates(state =>
                                state.map((item, index) => {
                                    if (index === 2) {
                                        return {...item, open: !item.open};
                                    }
                                    return item;
                                })
                            )
                        }
                        sx={{
                            backgroundColor: 'surface-container-high',
                            padding: '4px 4px 4px 16px',
                            height: '0',
                            minHeight: '0',
                            gap: '12px',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                alignItems: 'center',
                                margin: 0
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {}
                        }}>
                        <Typography variant="title" size="medium" width="100%">
                            {userSetDBAnalysisConfigurations[2].category}
                        </Typography>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                e.stopPropagation();
                                handleNext(3);
                            }}>
                            Edit
                        </Button>
                    </AccordionSummary>
                    <Box px={3} pb={3}>
                        {userSetDBAnalysisConfigurations[2].parameters.map((param: any) => {
                            return (
                                <div>
                                    <Typography variant="title" size="large" my={2}>
                                        {param.name}
                                    </Typography>
                                    {param.values.map((item: any) => {
                                        return (
                                            <Box display="flex" gap={1.5} mb={1}>
                                                <Typography variant="title" size="medium" fontWeight={600} display="left-align" width={390}>
                                                    {item.name}
                                                </Typography>
                                                {Array.isArray(item.value) ? (
                                                    <ol style={{margin: 0, paddingLeft: '1.2rem'}}>
                                                        {item.value.map((val: string) => (
                                                            <li>
                                                                <Typography variant="body" size="large">
                                                                    {val}
                                                                </Typography>
                                                            </li>
                                                        ))}
                                                    </ol>
                                                ) : (
                                                    <Typography variant="body" size="large">
                                                        {item.value}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </Box>
                </Accordion>
            </Box>
        </Box>
    );
};

export default DBReview;
